export type AudioPermissionKind = 'microphone' | 'systemAudio' | 'screenRecording'
export type SystemAudioActionKind = 'request' | 'retry'
export type ScreenRecordingActionKind = 'request' | 'restart' | 'settings' | 'retry'

/**
 * Which permission to prompt for next: mic first, then the runtime's far-side
 * permission (CATAP system audio or Screen Recording). Shared by toast /
 * banner / meeting card / Settings so the ordering cannot drift.
 */
export function resolveNextPermission(missing: {
  micMissing: boolean
  systemAudioMissing: boolean
  screenRecordingMissing: boolean
}): AudioPermissionKind | null {
  if (missing.micMissing) {
    return 'microphone'
  }
  if (missing.systemAudioMissing) {
    return 'systemAudio'
  }
  if (missing.screenRecordingMissing) {
    return 'screenRecording'
  }
  return null
}

type LabelVariant = 'button' | 'row'

const SCREEN_RECORDING_ACTION_LABELS: Record<
  Exclude<ScreenRecordingActionKind, 'request'>,
  Record<LabelVariant, string>
> = {
  restart: { button: 'Quit & Reopen', row: 'Quit and reopen to enable screen recording' },
  settings: { button: 'Open System Settings', row: 'Open Screen Recording settings' },
  retry: { button: 'Retry Permission Check', row: 'Retry screen recording permission check' },
}

const SYSTEM_AUDIO_RETRY_LABELS: Record<LabelVariant, string> = {
  button: 'Retry Permission Check',
  row: 'Retry system audio permission check',
}

/**
 * Canonical wording for the non-request Screen Recording actions. Only the
 * `request` copy is surface-specific ("Allow access" vs "Allow Screen
 * Recording"), so callers pass it in; `row` is the meeting card's toggle-row
 * phrasing, `button` everything else.
 */
export function screenRecordingActionLabel(
  actionKind: ScreenRecordingActionKind,
  requestLabel: string,
  variant: LabelVariant = 'button',
): string {
  return actionKind === 'request' ? requestLabel : SCREEN_RECORDING_ACTION_LABELS[actionKind][variant]
}

/** System-audio counterpart of {@link screenRecordingActionLabel}. */
export function systemAudioActionLabel(
  actionKind: SystemAudioActionKind,
  requestLabel: string,
  variant: LabelVariant = 'button',
): string {
  return actionKind === 'request' ? requestLabel : SYSTEM_AUDIO_RETRY_LABELS[variant]
}
